/**
 * Document viewer store.
 *
 * Manages a by-id map of loaded document artifacts, which document is
 * currently active in the viewer, and a "pending open" for the case where
 * an openDocument intent arrives before the document_created event has
 * populated the map.
 *
 * The store is conversation-aware: Chat.tsx calls closeForConversationSwitch
 * with the new conversation id on every switch, and openDocument rejects
 * intents whose conversationId doesn't match (stale-intent guard).
 *
 * SURFACE-KEYED. Two chat surfaces can be mounted at once — one per workspace
 * pane today, one per open tab under the live-tabs design — and each has its own
 * open document and its own conversation. Those live in `bySurface`, keyed by
 * `useSurfaceId()`. Before that, the second surface's conversation id overwrote
 * the first's, and the stale-intent guard then silently rejected every
 * openDocument the first surface asked for.
 *
 * Deliberately NOT keyed: `documentsById` is a content cache, so sharing it is
 * the point, and `panelWidth` is a user preference.
 */
import { create } from 'zustand';

/**
 * Full content of a document artifact, stored by id.
 *
 * For `kind: 'image'` the body is the image's URL (server path, object URL or
 * data URL), not its bytes.
 */
export interface DocumentArtifact {
  id: string;
  name: string;
  kind: 'markdown' | 'code' | 'html' | 'image';
  language: string | null;
  body: string;
}

// ---------------------------------------------------------------------------
// Panel width
// ---------------------------------------------------------------------------
//
// The viewer width lives in the store (not a per-component usePanelResize) so
// that BOTH the panel and the fixed-position chat input bar can read the same
// live value: the bar offsets its right edge by the panel width so it slides
// over with the conversation instead of staying pinned under the panel.

/** localStorage key for the persisted panel width (v2: supersedes the old usePanelResize key). */
const PANEL_WIDTH_STORAGE_KEY = 'chat-document-viewer-width-v2';

/** Smallest the viewer may be dragged. */
const PANEL_MIN_WIDTH = 360;

/** Keep at least this much room for the chat column when clamping the max. */
const CHAT_MIN_RESERVE = 360;

/**
 * Width of the (expanded) app sidebar — must match AppRouter's expanded sidebar
 * spacer. The chat layout is already inset by this much, so the panel max must
 * subtract it (use the larger expanded value so the reserve holds in both
 * sidebar states) to keep CHAT_MIN_RESERVE actually available to the chat column.
 */
const SIDEBAR_ALLOWANCE = 240;

/** Fallback width used in non-browser (SSR/test-without-window) contexts. */
const PANEL_FALLBACK_WIDTH = 480;

/** Clamp a requested width to [MIN, viewportWidth - sidebar - chat reserve]. */
function clampPanelWidth(width: number): number {
  if (typeof window === 'undefined') return width;
  const max = Math.max(PANEL_MIN_WIDTH, window.innerWidth - SIDEBAR_ALLOWANCE - CHAT_MIN_RESERVE);
  return Math.min(max, Math.max(PANEL_MIN_WIDTH, width));
}

/** Default width: half the viewport, clamped. */
function defaultPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_FALLBACK_WIDTH;
  return clampPanelWidth(Math.round(window.innerWidth * 0.5));
}

/**
 * The user's "desired" width — the persisted value (un-clamped) if present and
 * valid, otherwise the 50% default. Kept separate from the effective (clamped)
 * width so widening the window restores the user's chosen size rather than
 * leaving it stuck at a viewport-narrowed value.
 */
function readDesiredWidth(): number {
  if (typeof window === 'undefined') return PANEL_FALLBACK_WIDTH;
  try {
    const raw = window.localStorage.getItem(PANEL_WIDTH_STORAGE_KEY);
    if (raw !== null) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch {
    /* localStorage unavailable — fall through to the computed default */
  }
  return defaultPanelWidth();
}

/** Initial effective width: the desired width clamped to the current viewport. */
function initialPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_FALLBACK_WIDTH;
  return clampPanelWidth(readDesiredWidth());
}

/** One chat surface's view state: what it has open, and for which conversation. */
export interface SurfaceViewerState {
  activeDocumentId: string | null;
  pendingOpen: { id: string; conversationId: string } | null;
  /** The id of the conversation that is currently active; set via closeForConversationSwitch. */
  currentConversationId: string | null;
}

/** A surface that has never been touched: nothing open, no conversation claimed. */
export const EMPTY_SURFACE: SurfaceViewerState = {
  activeDocumentId: null,
  pendingOpen: null,
  currentConversationId: null,
};

interface DocumentViewerState {
  /** Shared content cache — every surface reads the same bodies. */
  documentsById: Record<string, DocumentArtifact>;
  /** Per-surface view state, keyed by `useSurfaceId()`. */
  bySurface: Record<string, SurfaceViewerState>;
  /** Current effective viewer width in px (shared by the panel and the chat input bar). */
  panelWidth: number;
  /** Set the viewer width (clamped) and persist it to localStorage. */
  setPanelWidth: (width: number) => void;
  /**
   * Re-derive the effective width from the persisted desired width, clamped to
   * the current viewport. Call on window resize so the chat-bar offset never
   * exceeds the available space; widening restores the user's chosen width.
   */
  reclampPanelWidth: () => void;
  /**
   * Add/replace a document in the shared map, and resolve it for EVERY surface
   * that was waiting on it — two surfaces can request the same document, and
   * each resolves independently.
   */
  upsertDocument: (doc: DocumentArtifact) => void;
  /**
   * Open a document by id for a given conversation. If the doc isn't loaded
   * yet, record a pending open (scoped to conversationId) and resolve when it
   * arrives via upsertDocument.
   *
   * Stale-intent guard: if conversationId is a non-empty string AND
   * currentConversationId is set AND they differ, the call is a no-op.
   * Empty conversationId (card-click path) is always allowed.
   */
  openDocument: (surfaceId: string, id: string, conversationId: string) => void;
  /** Close the viewer on one surface (clears its activeDocumentId only). */
  closeDocument: (surfaceId: string) => void;
  /**
   * Clear the open/pending state on conversation switch and record the new
   * conversation id. Keeps loaded document bodies intact.
   */
  closeForConversationSwitch: (surfaceId: string, conversationId: string | null) => void;
  /** Reset everything including currentConversationId. */
  reset: () => void;
}

export const useDocumentViewerStore = create<DocumentViewerState>((set, get) => ({
  documentsById: {},
  bySurface: {},
  panelWidth: initialPanelWidth(),

  setPanelWidth: (width: number): void => {
    const clamped = clampPanelWidth(width);
    try {
      window.localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(clamped));
    } catch {
      /* localStorage unavailable — width still updates in-memory */
    }
    set({ panelWidth: clamped });
  },

  reclampPanelWidth: (): void => {
    // Re-derive from the persisted desired width (not the current effective
    // value) so widening the window restores the user's chosen size. Does not
    // persist — this is a viewport-derived adjustment, not a user choice.
    set((s) => {
      const clamped = clampPanelWidth(readDesiredWidth());
      return clamped === s.panelWidth ? s : { panelWidth: clamped };
    });
  },

  upsertDocument: (doc: DocumentArtifact): void => {
    set((s) => {
      // Resolve the arrival for every surface waiting on this id, not just one:
      // both panes can have asked for the same document.
      const bySurface: Record<string, SurfaceViewerState> = {};
      for (const [surfaceId, view] of Object.entries(s.bySurface)) {
        bySurface[surfaceId] =
          view.pendingOpen !== null && view.pendingOpen.id === doc.id
            ? { ...view, activeDocumentId: doc.id, pendingOpen: null }
            : view;
      }
      return { documentsById: { ...s.documentsById, [doc.id]: doc }, bySurface };
    });
  },

  openDocument: (surfaceId: string, id: string, conversationId: string): void => {
    const { documentsById, bySurface } = get();
    const view = bySurface[surfaceId] ?? EMPTY_SURFACE;
    // Reject stale intents: non-empty conv id that doesn't match THIS surface's
    // current one. Scoped per surface, so the other pane's conversation switch
    // can no longer reject this pane's opens.
    if (
      conversationId !== '' &&
      view.currentConversationId !== null &&
      conversationId !== view.currentConversationId
    ) {
      return;
    }
    const next: SurfaceViewerState =
      documentsById[id] !== undefined
        ? { ...view, activeDocumentId: id, pendingOpen: null }
        : { ...view, pendingOpen: { id, conversationId } };
    set({ bySurface: { ...bySurface, [surfaceId]: next } });
  },

  closeDocument: (surfaceId: string): void => {
    set((s) => ({
      bySurface: {
        ...s.bySurface,
        [surfaceId]: { ...(s.bySurface[surfaceId] ?? EMPTY_SURFACE), activeDocumentId: null },
      },
    }));
  },

  closeForConversationSwitch: (surfaceId: string, conversationId: string | null): void => {
    set((s) => ({
      bySurface: {
        ...s.bySurface,
        [surfaceId]: {
          activeDocumentId: null,
          pendingOpen: null,
          currentConversationId: conversationId,
        },
      },
    }));
  },

  reset: (): void => {
    set({ documentsById: {}, bySurface: {} });
  },
}));

/** One surface's view state, or the empty one — never undefined. */
export function surfaceView(
  state: { bySurface: Record<string, SurfaceViewerState> },
  surfaceId: string
): SurfaceViewerState {
  return state.bySurface[surfaceId] ?? EMPTY_SURFACE;
}
